/**
 * Resolves once the browser has recorded `element`'s paint as the page's
 * Largest Contentful Paint, or after `timeoutMs`, whichever comes first.
 *
 * Kept as a copy of apps/shared/shellPaint.ts so agent-site typecheck does not
 * need tooling/typescript/agent-site.json to list that shared file (that path
 * filter also fires Deploy API on Hetzner). Keep the two in sync.
 *
 * index.html paints a static copy of the page's largest text before the app's
 * JavaScript runs, and React's first render replaces it with an identical node.
 * Chrome records a new LCP only for a strictly larger element, and a static node
 * that React removes before Chrome has recorded its paint (a fast machine, or a
 * font still loading) never counts: the app's later copy becomes the LCP. The
 * app therefore waits for the record before rendering. Where it matters that is
 * a frame or two; on a slow phone the record exists long before the app's code
 * has loaded, and the promise resolves at once.
 *
 * Resolves immediately when there is no element, when the browser does not
 * report LCP (Safari, older Firefox), or when the page is hidden (hidden pages
 * paint nothing, so the app must never wait for a paint there).
 */
export function afterShellPaint(element: Element | null, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const supported = typeof PerformanceObserver !== 'undefined'
      && (PerformanceObserver.supportedEntryTypes ?? []).includes('largest-contentful-paint');
    if (!element || !supported || document.visibilityState !== 'visible') {
      resolve();
      return;
    }
    const observer = new PerformanceObserver((list) => {
      if (list.getEntries().some((entry) => (entry as PerformanceEntry & { element?: Element | null }).element === element)) {
        done();
      }
    });
    const timer = setTimeout(done, timeoutMs);
    // Called only from the observer's and the timer's callbacks, which run
    // after both bindings exist.
    function done() {
      clearTimeout(timer);
      observer.disconnect();
      resolve();
    }
    // `buffered` delivers a paint recorded before this call, too.
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  });
}
